import {
  Alert,
  Box,
  Button,
  Modal,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { rutas } from "../../rutas/rutas";
import { getCookie } from "../../utils/getCookie";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "100%", sm: "70%" },
  height: { xs: "80%", sm: "70%" },
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  display: "flex",
};

export const ModalPool = ({ handleClose, open }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [usuario, setUsuario] = useState({});
  const { informacion } = useSelector((state) => state.userSlice);

  const [acumulador, setAcumulador] = useState(0);
  const [estimated, setEstimated] = useState(0);
  const [open2, setOpen2] = useState(false);
  const [error, setError] = React.useState(false);
  const [errorText, setErrorText] = React.useState("Error");

  const nf0 = useMemo(() => new Intl.NumberFormat("en-US"), []);
  const nf2 = useMemo(
    () => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    []
  );

  const handleClose2 = () => setOpen2(false);
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  // Headers (English + emojis)
  const columns = [
    { id: "usuario", label: "👤 USERS", minWidth: 120 },
    { id: "posicion", label: "🏆 POSITION", minWidth: 100 },
    { id: "puntaje", label: "⚡ POWER", minWidth: 100 },
    { id: "aprox",   label: "🎯 EST. HUESO", minWidth: 140 },
  ];

  const handleMirar = () => {
    const tokenJWT = getCookie("token");
    axios
      .get(rutas.mirarPool, { headers: { token: tokenJWT } })
      .then((res) => {
        if (!res.data?.stake) return;

        const totalPower = res.data.stake.reduce((acc, s) => acc + (Number(s?.cantidad) || 0), 0);
        const members = res.data.stake.length;
        const poolTotal = members * 230; // 230 per member in pool

        const row = res.data.stake.map(({ nombre, posicion, cantidad }) => {
          const powerNum = Number(cantidad) || 0;
          const share = totalPower > 0 ? powerNum / totalPower : 0;
          const approxNum = 150 + share * poolTotal; // base 150 + pool share
          return {
            usuario: nombre,
            posicion,
            puntaje: nf0.format(powerNum),
            aprox: nf2.format(approxNum),
          };
        });

        setAcumulador(totalPower);
        setUsuario(res.data.userStake);
        setRows(row);

        // Current user's estimate for the right panel
        const myPower = Number(res.data.userStake?.cantidad || 0);
        const myShare = totalPower > 0 ? myPower / totalPower : 0;
        const myApprox = 150 + myShare * poolTotal;
        setEstimated(myApprox > 0 ? Number(myApprox.toFixed(2)) : 0);
      })
      .catch(() => {});
  };

  const handleIngresar = () => {
    const tokenJWT = getCookie("token");
    axios
      .get(rutas.ingresoStakePool, { headers: { token: tokenJWT } })
      .then(() => {
        handleMirar();
      })
      .catch((error) => {
        setErrorText(error?.response?.data?.error);
        setError(true);
        setOpen2(true);
      });
  };

  useEffect(() => {
    handleMirar();
  }, []);

  const members = rows.length;
  const poolTotal = members * 230;

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <Box sx={{ ...style, flexDirection: { xs: "column-reverse", md: "row" } }}>
        {/* Table */}
        <Paper
          sx={{
            width: { xs: "95%", md: "50%" },
            height: { md: "40%", lg: "100%" },
            backgroundColor: "rgba(255, 255, 255, 0.1)",
          }}
        >
          <TableContainer
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              height: "90%",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={`head-${column.id}`}
                      align="center"
                      sx={{
                        background: "#0E1314",
                        color: "#00FFFF",
                        fontWeight: "bold",
                        textShadow: "0 0 8px #00FFFF",
                        letterSpacing: "1px",
                        fontSize: "0.95rem",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={`${row.usuario}-${row.posicion}`}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "rgba(255,255,255,0.05)" },
                        "&:nth-of-type(even)": { backgroundColor: "rgba(255,255,255,0.02)" },
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={`${column.id}-${row.usuario}-${row.posicion}`}
                          align="center"
                          sx={{ color: "white" }}
                        >
                          {row[column.id]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ height: "35px", display: { xs: "none", md: "block" } }}
          />
        </Paper>

        {/* Right panel */}
        <Box
          sx={{
            ml: { md: 1 },
            color: "white",
            width: { xs: "95%", md: "50%" },
            height: { xs: "55%", md: "90%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{ mb: 2, fontSize: "1.6rem", textShadow: "0 0 10px orange", fontWeight: 800 }}
            textAlign="center"
          >
            Bone Power Pool + Electric Energy
          </Typography>

          <Typography sx={{ fontSize: "1.05rem", textShadow: "0 0 6px #FFD580" }}>
            Instructions: for each user in the pool, <b>230</b> bones are added to the total bones to distribute.
          </Typography>

          <Typography sx={{ fontSize: "1.05rem", mt: 1 }}>
            How many $HUESO do I get every 24 hours?<br />
            By participating you already get a base reward of <b>150</b> bones, plus what you earn from the POOL.<br />
            The pool is split among all members, but participants with more power get the best rewards; the power of all your NFTs is summed.
          </Typography>

          <Typography sx={{ textTransform: "uppercase", mt: 1 }}>
            TOTAL POOL: <b>{nf0.format(poolTotal)}</b> &nbsp;–&nbsp; ESTIMATED REWARD: <b>{nf2.format(estimated)}</b> &nbsp;–&nbsp; Previous reward: {informacion?.rewardPool}
          </Typography>

          <Box
            sx={{
              width: "100%",
              mt: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              flexDirection: "column",
            }}
          >
            <Typography>
              {(informacion?.costoPool || 20)} Energy
            </Typography>
            <Button
              disabled={informacion?.energia?.actual < (informacion?.costoPool || 20) || informacion?.pool}
              onClick={handleIngresar}
              variant="contained"
              sx={{
                px: 4,
                borderRadius: 999,
                boxShadow: "0 0 12px rgba(255,165,0,0.8)",
                fontWeight: 800,
              }}
            >
              ENTER
            </Button>
          </Box>
        </Box>

        <Stack spacing={2} sx={{ width: "100%", position: "absolute" }}>
          <Snackbar open={open2} autoHideDuration={6000} onClose={handleClose2}>
            {error ? (
              <Alert onClose={handleClose2} severity="error" variant="filled" sx={{ width: "100%" }}>
                {errorText}
              </Alert>
            ) : (
              <Alert onClose={handleClose2} severity="success" variant="filled" sx={{ width: "100%" }}>
                Success
              </Alert>
            )}
          </Snackbar>
        </Stack>
      </Box>
    </Modal>
  );
};
